import type {
  MassFilterField,
  MassFilterFieldDefinition,
  MassFilterNormalizeOptions,
} from '../types/mass-filters.types';

/**
 * Фильтры списка заявок, принимающие набор значений, и правило сравнения
 * для каждого. Одно описание на валидацию, нормализацию и построение
 * запроса: разъехавшись, они дают фильтр, который проходит проверку, но
 * ищет не так, как ожидает пользователь.
 */
export const MASS_FILTER_FIELDS: readonly MassFilterFieldDefinition[] = [
  { field: 'contract', caseInsensitive: false },
  // carVin не нормализуется при записи: в поле встречаются оба регистра
  { field: 'vin', caseInsensitive: true },
];

/** Имена массовых фильтров — когда правило сравнения не нужно. */
export const MASS_FILTER_FIELD_NAMES: readonly MassFilterField[] =
  MASS_FILTER_FIELDS.map(({ field }) => field);

/** Правило сравнения по имени поля — для обращения из построителя запроса. */
export const MASS_FILTER_CASE_INSENSITIVE: Readonly<
  Record<MassFilterField, boolean>
> = MASS_FILTER_FIELDS.reduce(
  (rules, { field, caseInsensitive }) => ({
    ...rules,
    [field]: caseInsensitive,
  }),
  {} as Record<MassFilterField, boolean>,
);

/**
 * Максимальное количество значений в одном массовом фильтре списка заявок
 * (номера договоров, VIN). Проверяется после нормализации набора.
 */
export const MASS_FILTER_VALUES_LIMIT = 1000;

/**
 * Максимальная длина одного значения массового фильтра. VIN — 17 символов,
 * номера договоров короче, поэтому 64 берём с запасом: он отсекает мусорную
 * вставку, не мешая настоящим значениям. Проверяется и на клиенте, и на
 * сервере, поэтому константа общая.
 */
export const MASS_FILTER_VALUE_MAX_LENGTH = 64;

/**
 * Порог для адресной строки: начиная с этого количества значений в массовых
 * фильтрах, набор в адрес страницы не пишется — длинный URL ломает
 * перезагрузку страницы.
 */
export const MASS_FILTER_URL_VALUES_LIMIT = 20;

/**
 * Безопасный размер сериализованных фильтров (rqQuery) для передачи в URL.
 * Предохранитель на случай аномально длинных значений: nginx по умолчанию
 * отдаёт 414 на строку запроса длиннее 8 КБ.
 */
export const RQ_QUERY_URL_SAFE_LIMIT = 6144;

/**
 * Нормализация набора значений массового фильтра: обрезка пробелов,
 * отбрасывание пустых значений, дедупликация (остаётся первое вхождение).
 * Значение попадает в результат в том виде, в каком его ввёл пользователь:
 * регистр не меняется, иначе в поле и в чипе фильтра отображался бы не его
 * текст. Дедупликация идёт ровно по тому правилу, по которому потом будет
 * сравнение: при caseInsensitive `abc` и `ABC` — одно значение, без него
 * `c1` и `C1` остаются разными и оба участвуют в поиске. Вызывается и на
 * клиенте, и на сервере, поэтому результат обязан совпадать с обеих сторон.
 */
export function normalizeMassFilterValues(
  values: readonly string[],
  options: MassFilterNormalizeOptions = {},
): string[] {
  const normalized: string[] = [];
  const seen = new Set<string>();

  for (const rawValue of values) {
    if (typeof rawValue !== 'string') {
      continue;
    }

    const trimmed = rawValue.trim();

    if (!trimmed) {
      continue;
    }

    const seenKey = options.caseInsensitive ? trimmed.toUpperCase() : trimmed;

    if (seen.has(seenKey)) {
      continue;
    }

    seen.add(seenKey);
    normalized.push(trimmed);
  }

  return normalized;
}
